/**
 * Modal Component
 *
 * A reusable modal dialog for displaying content in an overlay.
 * - Uses React Portal to render above all content
 * - Supports custom title, icon, close button, footer, z-index, and width
 * - Handles click outside to close
 * - Accepts children for modal content
 *
 * Props:
 *   - isOpen: boolean (controls visibility)
 *   - title: string (optional, modal header)
 *   - icon: ReactNode (optional, icon element for header)
 *   - onClose: function (called when modal closes)
 *   - children: ReactNode (modal content)
 *   - footer: ReactNode (optional, footer content)
 *   - zIndex: number (custom stacking order)
 *   - showClose: boolean (show/hide close button)
 *   - width: string (Tailwind max-width class)
 */
import { X } from "lucide-react";
import React, { useRef } from "react";
import { createPortal } from "react-dom";
export interface ModalProps {
  isOpen: boolean;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  zIndex?: number;
  showClose?: boolean;
  width?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  icon,
  onClose,
  children,
  footer,
  zIndex = 999999,
  showClose = true,
  width = "max-w-lg",
}) => {
  const mouseDownOnBackdrop = useRef(false);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/10"
      style={{ zIndex }}
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
        mouseDownOnBackdrop.current = false;
      }}
    >
      <div
        className={`bg-card rounded-md w-full ${width} relative border border-border max-h-[90vh] flex flex-col`}
        style={{ zIndex: zIndex + 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || icon || showClose) && (
          <div className="flex items-center justify-between px-5 py-3 border-b border-border/50 flex-shrink-0">
            <div className="flex items-center gap-3">
              {icon && icon}
              {title && (
                <h3 className="text-base font-semibold text-foreground">
                  {title}
                </h3>
              )}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto flex-grow">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-border/50 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
